import type { User } from '@/contexts/AuthContext';
import type { GateStatus, GateStatusQueryOptions, StatusGateOptions } from '@/services/statusGate/statusGate.types';
import { type QueryKey, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useLocation } from 'wouter';

/** Stable per-account id for status query keys; impersonation swaps it. */
export const statusAccountId = (user: Pick<User, 'id' | 'authAccountId'> | null): string | null =>
  user ? (user.authAccountId ?? user.id) : null;

/** The gate's rule: the step is required and not yet done. */
export const isGatePending = (status: GateStatus | undefined): boolean => !!status?.required && !status.completed;

/** Re-checked on every guarded mount, never retried, and offline rejects instead of pausing. */
export const gateStatusQueryOptions = <T extends GateStatus>(
  queryKey: QueryKey,
  queryFn: () => Promise<T>
): GateStatusQueryOptions<T> => ({
  queryKey,
  queryFn,
  staleTime: 0,
  retry: false,
  networkMode: 'always',
});

/**
 * Redirects to `redirectTo` while the status is required and not completed.
 * Returns true while the guard must block rendering (checking or redirecting).
 */
export const useStatusGate = <T extends GateStatus>({
  queryOptions,
  enabled,
  redirectTo,
  errorMessage,
}: StatusGateOptions<T>): boolean => {
  const [, navigate] = useLocation();
  const { data: status, isError, isFetchedAfterMount } = useQuery({ ...queryOptions, enabled });

  // Cached data (possibly another identity's or pre-completion) is never trusted:
  // wait until this mount's own request for the current key has settled.
  const isChecking = enabled && !isFetchedAfterMount;
  // Fail open: a failed status check never blocks access.
  const mustRedirect = enabled && !isChecking && !isError && isGatePending(status);

  useEffect(() => {
    if (mustRedirect) navigate(redirectTo);
  }, [mustRedirect, navigate, redirectTo]);

  useEffect(() => {
    if (isError) console.error(errorMessage);
  }, [isError, errorMessage]);

  return isChecking || mustRedirect;
};
